import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { initPluginRuntime } from './PluginRuntime';

// Provee la lista de plugins activos + su config resuelta. Dormido por defecto:
// si el kill switch global esta off o no hay sesion, entrega lista vacia y nada se renderiza.
const PluginContext = createContext({ enabled: false, loaded: false, plugins: [], getPluginsForSurface: () => [], refresh: () => {} });

export const PluginProvider = ({ children }) => {
  const { apiCall } = useApi();
  const { token } = useAuth();
  // loaded: ya se consulto al menos una vez (para no mostrar "no disponible" mientras carga).
  const [state, setState] = useState({ enabled: false, loaded: false, plugins: [] });
  const initRef = useRef(false);

  // Inicializa los singletons del runtime una sola vez (para bundles dinamicos).
  if (!initRef.current && typeof window !== 'undefined') {
    initPluginRuntime();
    initRef.current = true;
  }

  const refresh = useCallback(async (skillId = null) => {
    if (!token) {
      setState({ enabled: false, loaded: true, plugins: [] });
      return;
    }
    try {
      const qs = skillId != null ? `?skillId=${encodeURIComponent(skillId)}` : '';
      const res = await apiCall(`/plugins/active${qs}`);
      const data = res?.data || res; // apiCall puede devolver el json completo
      if (data && Array.isArray(data.plugins)) {
        setState({ enabled: !!data.enabled, loaded: true, plugins: data.plugins });
      } else {
        setState({ enabled: false, loaded: true, plugins: [] });
      }
    } catch (error) {
      // Dormido / contenido: cualquier fallo deja el sistema sin plugins, sin afectar el app.
      setState({ enabled: false, loaded: true, plugins: [] });
    }
  }, [apiCall, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getPluginsForSurface = useCallback((surfaceId) => {
    if (!state.enabled) return [];
    return state.plugins
      .map((p) => {
        const surface = (p.surfaces || []).find((s) => s.id === surfaceId);
        return surface ? { plugin: p, surface } : null;
      })
      .filter(Boolean);
  }, [state]);

  return (
    <PluginContext.Provider value={{ ...state, getPluginsForSurface, refresh }}>
      {children}
    </PluginContext.Provider>
  );
};

export const usePlugins = () => useContext(PluginContext);

export default PluginContext;
